import type { AxiosError } from 'axios';

/**
 * Casts an unknown catch-block error to an AxiosError shape for API service use.
 * Use this in API service files instead of `error: any` to preserve type safety.
 */
export function asAxiosError(error: unknown): AxiosError<{ error?: string; errors?: string[]; message?: string }> {
  return error as AxiosError<{ error?: string; errors?: string[]; message?: string }>;
}

// Machine codes the backend renders on every route (the Discord beta gate sits
// on ApplicationController), translated once here so no call site shows them raw.
const ERROR_CODE_MESSAGES: Record<string, string> = {
  beta_access_revoked: "Your account isn't approved for the beta. Request beta access in our Discord server.",
  beta_access_unavailable: "We couldn't verify your beta access just now. Please try again in a minute.",
};

export function extractApiErrorMessage(error: unknown, fallback: string): string {
  const axiosError = asAxiosError(error);
  const payload = axiosError.response?.data;

  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    return payload.errors.join(', ');
  }

  if (payload?.error && ERROR_CODE_MESSAGES[payload.error]) {
    return ERROR_CODE_MESSAGES[payload.error];
  }

  return payload?.error || payload?.message || axiosError.message || fallback;
}

/**
 * An API failure that keeps WHY it failed, not just a sentence about it.
 *
 * withApiError used to flatten every failure to `new Error(message)`, so a
 * caller could not tell a paywall from a missing record and had to render the
 * server's raw sentence — which is how "Daily limit reached (3 chapters)" ended
 * up under a "Chapter not found" heading.
 */
export class ApiError extends Error {
  readonly status?: number;
  /** Machine-readable cause from the backend, e.g. 'daily_limit' | 'premium_required'. */
  readonly reason?: string;
  readonly requiredPlan?: string;
  readonly remainingToday?: number | null;

  constructor(message: string, details: {
    status?: number;
    reason?: string;
    requiredPlan?: string;
    remainingToday?: number | null;
  } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = details.status;
    this.reason = details.reason;
    this.requiredPlan = details.requiredPlan;
    this.remainingToday = details.remainingToday;
  }
}

function toApiError(error: unknown, fallback: string): ApiError {
  const axiosError = asAxiosError(error);
  const payload = axiosError.response?.data as
    | { reason?: string; required_plan?: string; remaining_today?: number | null }
    | undefined;

  return new ApiError(extractApiErrorMessage(error, fallback), {
    status: axiosError.response?.status,
    reason: payload?.reason,
    requiredPlan: payload?.required_plan,
    remainingToday: payload?.remaining_today,
  });
}

export async function withApiError<T>(
  fn: () => Promise<T>,
  message: string,
  options?: { suppressLog404?: boolean }
): Promise<T> {
  try {
    return await fn();
  } catch (error: unknown) {
    if (options?.suppressLog404 && asAxiosError(error).response?.status === 404) {
      throw toApiError(error, message);
    }
    console.error(message, error);
    throw toApiError(error, message);
  }
}
